import Modal from "@via/components/modal";
import { useCallback, useEffect, useState } from "react";
import { useBlocker, useNavigate } from "react-router-dom";

const message = "You have unsaved changes, do you really want to leave?";

/**
 * useBeforeUnload hook
 *
 * This hook sets up an event listener for the `beforeunload` event to warn the user
 * about unsaved changes or other conditions before they leave the page.
 *
 * @param shouldWarn - A boolean indicating whether the warning should be displayed.
 * @param message - The message to display in the warning dialog.
 */
export const useBeforeUnload = (shouldWarn: boolean, message: string): void => {
  useEffect(() => {
    const handleBeforeUnload = (event: BeforeUnloadEvent) => {
      if (shouldWarn) {
        event.preventDefault();
        event.returnValue = message;
        return message;
      }
    };

    window.addEventListener("beforeunload", handleBeforeUnload);

    return () => {
      window.removeEventListener("beforeunload", handleBeforeUnload);
    };
  }, [shouldWarn, message]);
};

/**
 * useNavigationBlocker hook
 *  This hook is used to block navigation when there are unsaved changes.
 *  It uses the `useBlocker` hook from `react-router-dom` to block navigation.
 *  It also uses the `useBeforeUnload` hook to warn the user about unsaved changes when they try to reload the page.
 *  It also provides a wrapped navigate function that will display a warning dialog before navigating to a new location.
 * @param isBlocked - A boolean indicating whether navigation should be blocked.
 * @param excludedPaths - An array of paths that should be excluded from blocking.
 * @returns An object containing the wrapped navigate function.
 */
export function useNavigationBlocker(
  isBlocked: boolean,
  excludedPaths: string[] = []
) {
  const [preventDuplicate, setPreventDuplicate] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [pendingNavigation, setPendingNavigation] = useState<{
    to?: string;
    blocker?: any;
    callback?: () => void;
  }>({});
  const navigate = useNavigate();
  useBeforeUnload(isBlocked, message);

  const blocker = useBlocker(({ nextLocation }) => {
    // Skip blocking if the next location is in the excluded paths
    return isBlocked && !excludedPaths.includes(nextLocation.pathname);
  });

  useEffect(() => {
    if (!preventDuplicate && blocker.state === "blocked") {
      setPendingNavigation({ blocker });
      setShowModal(true);
    } else {
      blocker?.proceed && blocker.proceed(); // Allow navigation and prevent duplicate blocking
      setPreventDuplicate(false);
    }
  }, [blocker, preventDuplicate]);

  const wrappedNavigate = useCallback(
    (to: string, callback?: () => void) => {
      if (isBlocked && !excludedPaths.includes(to)) {
        setShowModal(true);
        setPendingNavigation({ to, callback });
        return false;
      }
      navigate(to);
      callback?.();
      return true; // Allow navigation
    },
    [isBlocked, navigate]
  );

  const handleConfirm = async () => {
    setShowModal(false);
    if (pendingNavigation.to) {
      navigate(pendingNavigation.to);
      pendingNavigation.callback?.();
    } else if (pendingNavigation.blocker) {
      pendingNavigation.blocker.proceed();
    }
    setPreventDuplicate(true);
    setPendingNavigation({});
  };

  const handleCancel = async () => {
    setShowModal(false);
    pendingNavigation.blocker?.reset();
    setPendingNavigation({});
  };

  return {
    navigate: wrappedNavigate,
    NavigationBlockerModal: showModal ? (
      <Modal
        open={showModal}
        setOpen={setShowModal}
        title="Leave page without saving?"
        onConfirm={handleConfirm}
        onCancel={handleCancel}
        confirmText="Leave"
        size="small">
        <p className="text-sm leading-6 text-slate-600 pt-2 pb-5">
          All unsaved information will be lost.
        </p>
      </Modal>
    ) : null,
  };
}
